import React, { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import api, { glCodes } from '../services/api';

const invoicesApi = {
  list:   (params) => api.get('/invoices', { params }),
  upload: (fd)     => api.post('/invoices/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, d)  => api.put(`/invoices/${id}`, d),
  status: (id, s)  => api.post(`/invoices/${id}/status`, { status: s }),
  delete: (id)     => api.delete(`/invoices/${id}`),
  stats:  ()       => api.get('/invoices/stats'),
};

const STATUS_COLORS = {
  pending:  'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  paid:     'bg-blue-100 text-blue-800',
  rejected: 'bg-red-100 text-red-800',
};

function UploadZone({ onUploaded }) {
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(async (files) => {
    if (!files[0]) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', files[0]);
    try {
      const res = await invoicesApi.upload(fd);
      toast.success(`Invoice processed${res.data.ai_extracted ? ' — AI extracted data' : ''}`);
      if (res.data.anomaly_flag) toast(`⚠️ Anomaly detected: ${res.data.anomaly_reason}`, { icon: '⚠️', duration: 6000 });
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setUploading(false); }
  }, [onUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: false,
    accept: { 'application/pdf': [], 'image/jpeg': [], 'image/png': [] },
  });

  return (
    <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors mb-6 ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 bg-white'}`}>
      <input {...getInputProps()} />
      {uploading ? (
        <div className="flex flex-col items-center gap-2 text-blue-600">
          <svg className="animate-spin w-8 h-8" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
          <p className="text-sm font-medium">AI is extracting invoice data…</p>
        </div>
      ) : (
        <>
          <svg className="w-10 h-10 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
          <p className="text-sm text-gray-600 font-medium">Drop invoice here or <span className="text-blue-600">browse</span></p>
          <p className="text-xs text-gray-400 mt-1">PDF, JPG, or PNG · AI will extract all data automatically</p>
        </>
      )}
    </div>
  );
}

function InvoiceRow({ inv, onStatusChange, onDelete, glCodeList, onGLAssigned }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const assignGL = async (code) => {
    setSaving(true);
    try {
      await invoicesApi.update(inv.id, { gl_code: code || null });
      toast.success(code ? `GL ${code} assigned` : 'GL code cleared');
      onGLAssigned();
    } catch { toast.error('Failed to save GL code'); }
    finally { setSaving(false); }
  };

  return (
    <div className={`bg-white rounded-xl border ${inv.anomaly_flag ? 'border-orange-300' : 'border-gray-200'} overflow-hidden`}>
      <div className="px-5 py-4 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[inv.status]}`}>{inv.status}</span>
            {inv.ai_extracted && <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">AI extracted</span>}
            {inv.anomaly_flag && <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">⚠ Anomaly</span>}
            {inv.gl_code
              ? <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-mono">{inv.gl_code} · {inv.budget_category?.split(' - ')[1] || inv.budget_category}</span>
              : inv.budget_category && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{inv.budget_category}</span>
            }
          </div>
          <h3 className="font-semibold text-gray-900">{inv.vendor_name || 'Unknown Vendor'}</h3>
          <div className="flex gap-3 mt-1 text-xs text-gray-400 flex-wrap">
            {inv.invoice_number && <span>#{inv.invoice_number}</span>}
            {inv.invoice_date && <span>· {inv.invoice_date}</span>}
            {inv.due_date && <span>· Due {inv.due_date}</span>}
            {inv.building_name && <span>· {inv.building_name}</span>}
          </div>
          {inv.anomaly_flag && inv.anomaly_reason && (
            <p className="text-xs text-orange-600 mt-1">⚠ {inv.anomaly_reason}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-gray-900">${parseFloat(inv.total_amount || 0).toLocaleString('en-CA', { minimumFractionDigits: 2 })}</p>
          {inv.tax_amount && <p className="text-xs text-gray-400">incl. ${parseFloat(inv.tax_amount).toFixed(2)} tax</p>}
        </div>
      </div>

      <div className="px-5 pb-4 flex items-center gap-3 border-t pt-3">
        {inv.status === 'pending' && (
          <>
            <button onClick={() => onStatusChange(inv.id, 'approved')} className="text-xs px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg">Approve</button>
            <button onClick={() => onStatusChange(inv.id, 'rejected')} className="text-xs px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg">Reject</button>
          </>
        )}
        {inv.status === 'approved' && (
          <button onClick={() => onStatusChange(inv.id, 'paid')} className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Mark Paid</button>
        )}
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-gray-500 hover:text-gray-700 ml-auto">
          {expanded ? 'Hide details' : 'Show details'}
        </button>
        <button onClick={() => onDelete(inv.id)} className="text-xs text-red-500 hover:underline">Delete</button>
      </div>

      {expanded && (
        <div className="px-5 pt-3 pb-2 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-xs font-medium text-gray-500 shrink-0">GL Code</label>
            <select
              value={inv.gl_code || ''}
              onChange={e => assignGL(e.target.value)}
              disabled={saving}
              className="text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 max-w-xs">
              <option value="">— Unassigned —</option>
              {['Income','Operating Expense','Contingency Reserve Fund'].map(type => (
                <optgroup key={type} label={type}>
                  {(glCodeList || []).filter(g => g.type === type).map(g => (
                    <option key={g.code} value={g.code}>{g.code} – {g.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {saving && <span className="text-xs text-gray-400">Saving…</span>}
          </div>
        </div>
      )}

      {expanded && inv.line_items?.length > 0 && (
        <div className="px-5 pb-4">
          <table className="min-w-full text-xs">
            <thead><tr className="text-gray-400"><th className="text-left py-1">Description</th><th className="text-right py-1">Qty</th><th className="text-right py-1">Unit Price</th><th className="text-right py-1">Amount</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {inv.line_items.map((li, i) => (
                <tr key={i}>
                  <td className="py-1 text-gray-700">{li.description}</td>
                  <td className="py-1 text-right text-gray-500">{li.quantity || 1}</td>
                  <td className="py-1 text-right text-gray-500">${li.unit_price?.toFixed(2) || '—'}</td>
                  <td className="py-1 text-right text-gray-700 font-medium">${li.amount?.toFixed(2) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Invoices() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [glCodeList, setGLCodeList] = useState([]);

  const load = () => {
    invoicesApi.list({ status: filterStatus }).then(r => setList(r.data)).catch(() => toast.error('Failed to load'));
    invoicesApi.stats().then(r => setStats(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [filterStatus]);
  useEffect(() => { glCodes.list().then(r => setGLCodeList(r.data)).catch(() => {}); }, []);

  const handleStatusChange = async (id, status) => {
    await invoicesApi.status(id, status);
    toast.success(`Invoice ${status}`); load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    await invoicesApi.delete(id); toast.success('Deleted'); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-500 text-sm mt-1">AI extracts data automatically from photos or PDFs</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Pending', value: stats.pending, sub: `$${stats.total_pending_amount?.toLocaleString('en-CA', {minimumFractionDigits:2})}`, color: 'bg-yellow-50 border-yellow-200' },
            { label: 'Approved', value: stats.approved, sub: `$${stats.total_approved_amount?.toLocaleString('en-CA', {minimumFractionDigits:2})}`, color: 'bg-green-50 border-green-200' },
            { label: 'Paid', value: stats.paid, sub: 'invoices', color: 'bg-blue-50 border-blue-200' },
            { label: 'Anomalies', value: stats.anomaly_count, sub: 'need review', color: stats.anomaly_count > 0 ? 'bg-orange-50 border-orange-300' : 'bg-gray-50 border-gray-200' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl p-4 border ${s.color}`}>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{s.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>
      )}

      <UploadZone onUploaded={load} />

      <div className="flex gap-3 mb-4">
        <select className="input w-44" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['pending','approved','paid','rejected'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">
          No invoices yet. Drop a PDF or photo above to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map(inv => <InvoiceRow key={inv.id} inv={inv} onStatusChange={handleStatusChange} onDelete={handleDelete} glCodeList={glCodeList} onGLAssigned={load} />)}
        </div>
      )}

      <style>{`.input{width:100%;border:1px solid #d1d5db;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;outline:none}.input:focus{border-color:#3b82f6}`}</style>
    </div>
  );
}
